import React, { SyntheticEvent, useRef } from "react";
import {
  BoxProps,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import Slider from "@mui/material/Slider";

const min = 8;
const max = 92;

const uploadImage = require("../assets/svg/Upload.png");
const completeImage = require("../assets/svg/Check.png");
export const SlideToConfirm = (props: {
  children: React.ReactNode;
  style: BoxProps["style"];
  loading?: boolean;
  onConfirm?: () => void;
  icon?: any;
  warning?: boolean;
  ripple?: boolean;
  orientation?: "horizontal" | "vertical";
  onReadyChange?: (ready: boolean) => void;
}) => {
  const [value, setValue] = React.useState(min);
  const [failed, setFailed] = React.useState(false);
  const [complete, setComplete] = React.useState(false);
  const [attempts, setAttempts] = React.useState(0);
  const theme = useTheme();
  const isVertical = props.orientation === "vertical";
  const sliderValue = isVertical ? max + min - value : value;

  const sliderRef = useRef<HTMLSpanElement>(null);

  const allStyles: BoxProps["style"] = props.style
    ? {
        ...props.style,
        boxSizing: "border-box",
        borderRadius: 2,
        border: 1,
        borderColor: "primary",
        overflow: "hidden",
      }
    : {};

  const stopProp = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  const handleChange = (event: Event, newValue: number | number[]) => {
    const rawValue = Array.isArray(newValue) ? newValue[0] : newValue;
    const nextValue = isVertical ? max + min - rawValue : rawValue;
    const clampedValue = Math.max(Math.min(nextValue, max), min);
    setValue(clampedValue);
    props.onReadyChange?.(clampedValue > max - 1);
  };
  const handleChangeCommitted = async (
    event: Event | SyntheticEvent<Element, Event>,
    value: number | number[]
  ) => {
    event.stopPropagation();
    const rawValue = Array.isArray(value) ? value[0] : value;
    const committedValue = isVertical ? max + min - rawValue : rawValue;

    if (committedValue > max - 1) {
      try {
        await props?.onConfirm?.();
        setComplete(true);
      } catch (err) {
        setFailed(true);
        console.log(err);
      }
      setTimeout(() => {
        setAttempts(attempts + 1);
        setValue(min);
        setComplete(false);
        setFailed(false);
        props.onReadyChange?.(false);
      }, 2000);
    } else {
      props.onReadyChange?.(false);
      setValue(min);
    }
  };

  return (
    <Box display={"flex"} alignItems="row" justifyItems="center">
      <Box
        display={"flex"}
        alignItems="column"
        justifyItems="center"
        flex={1}
      >
        <Box
          sx={allStyles}
          alignItems="center"
          justifyItems="center"
          display={"flex"}
          position="relative"
          onClick={stopProp}
          flex={1}
        >
          <Slider
            key={`slider-${attempts}-`}
            ref={sliderRef}
            disabled={props?.loading || complete}
            value={sliderValue}
            onChange={handleChange}
            onChangeCommitted={handleChangeCommitted}
            title={"Slide to confirm"}
            orientation={isVertical ? "vertical" : "horizontal"}
            track={isVertical ? false : "normal"}
            sx={{
              zIndex: 10001,
              flex: 1,
              height: isVertical ? "calc(100% - 36px)" : undefined,
              width: isVertical ? 56 : undefined,
              mt: isVertical ? "28px" : undefined,
              mb: isVertical ? "28px" : undefined,
              color: props.warning
                ? theme.palette.error.light
                : theme.palette.primary.main,
              pointerEvents: "none",
              "& .MuiSlider-valueLabel": { pointerEvents: "none" },
              "& .MuiSlider-root": { pointerEvents: "none" },
              "& .MuiSlider-mark": {
                pointerEvents: "none",
              },
              "& .MuiSlider-track": {
                pointerEvents: "none",
                color: isVertical ? "transparent" : theme.palette.secondary.main,
                backgroundColor: isVertical
                  ? "transparent"
                  : undefined,
                height: isVertical ? undefined : 56,
                width: isVertical ? 56 : undefined,
                margin: 0,
                border: "none",
                borderRadius: isVertical ? 2 : 8,
              },
              "& .MuiSlider-rail": {
                color: isVertical ? "transparent" : "transparent",
                backgroundColor: isVertical ? "transparent" : undefined,
                marginLeft: isVertical ? 0 : "9%",
                pointerEvents: "none",
                opacity: 1,
                width: isVertical ? 56 : undefined,
                borderRadius: isVertical ? 2 : 8,
              },

              "& .MuiSlider-thumb": {
                pointerEvents: "all",
                backgroundColor: isVertical
                  ? theme.palette.common.white
                  : undefined,
                backgroundImage: `url(${
                  props?.loading
                    ? null
                    : complete
                    ? completeImage
                    : props.icon
                })`,
                backgroundSize: "20px 20px",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                height: isVertical ? 56 : 56,
                width: isVertical ? 56 : 70,
                borderRadius: 2,
                border: "none",
                boxShadow: isVertical
                  ? "0 4px 12px rgba(0, 0, 0, 0.25)"
                  : undefined,
                "&:focus, &:hover, &.Mui-active": {
                  "@media (hover: none)": {},
                },
                "&::after":
                  props.ripple && !props.loading && !complete
                    ? {
                        content: '""',
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        border: "3px solid rgba(16, 24, 40, 0.9)",
                        pointerEvents: "none",
                        animation: "sliderThumbRipple 2s infinite",
                      }
                    : undefined,
              },
              "@keyframes sliderThumbRipple": {
                "0%": {
                  transform: "translate(-50%, -50%) scale(0.8)",
                  opacity: 0.6,
                },
                "100%": {
                  transform: "translate(-50%, -50%) scale(1.8)",
                  opacity: 0,
                },
              },
            }}
          ></Slider>
          <Box
            sx={{
              pointerEvents: "none",
              zIndex: 10000,
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }}
            position="absolute"
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={2}
          >
            {props.children}
          </Box>
          <Box
            sx={{
              pointerEvents: "none",
              zIndex: 10003,
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }}
            position="absolute"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {props?.loading ? (
              <CircularProgress size={22} sx={{ color: "white" }} />
            ) : null}
          </Box>
        </Box>
        {failed ? (
          <Typography variant="subtitle1" color="error">
            Request Failed.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};
